import { useContext, useState } from 'react';
import { otelContext } from './OtelContextProvider';
import { Context, Span, SpanOptions, Tracer } from '@opentelemetry/api';
import { useLocation } from 'react-router-dom';
import { SpanRegistrar } from '@trp-ta-nitro/otel-browser-js';
import * as Names from './names';
import { registrarContext } from './SpanRegistrarProvider';

// useOtelContext
// ==============

export function useOtelContext(context?: Context) {
  const providedContext = useContext(otelContext);
  return context ?? providedContext;
}

// useSpanRegistrar
// ================

export function useSpanRegistrar(): SpanRegistrar {
  const registrar = useContext(registrarContext);
  if (!registrar) {
    throw new Error(
      'SpanRegistrar unavailable. Is SpanRegistrarProvider configured correctly?',
    );
  }
  return registrar;
}

// useRouteSpan
// ============

export function useRouteSpan(): Span | undefined {
  const registrar = useSpanRegistrar();
  const location = useLocation();
  return registrar.getSpan(Names.pageSpanFromLocation(location));
}

// useSpan
// =======

interface SpanExt extends Span {
  afterEnd?: () => void;
}

function initialSpan(
  tracer: Tracer,
  registrar: SpanRegistrar,
  name: string,
  options?: SpanOptions,
  context?: Context,
): SpanExt {
  const spanExt: SpanExt = tracer.startSpan(name, options, context);
  const origEnd = spanExt.end.bind(spanExt);
  spanExt.end = (endTime) => {
    origEnd(endTime);
    spanExt.afterEnd?.();
  };
  registrar.registerSpan(name, spanExt);
  return spanExt;
}

export function useSpan(
  tracer: Tracer,
  name: string,
  options?: SpanOptions,
  context?: Context,
): Span | undefined {
  const registrar = useSpanRegistrar();
  context = useOtelContext(context);
  const [span, setSpan] = useState<SpanExt | undefined>(() =>
    initialSpan(tracer, registrar, name, options, context),
  );
  if (span) {
    span.afterEnd = () => setSpan(undefined);
  }
  return span;
}
